import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import { ConfigProvider, theme as antdTheme } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import { store } from './store';
import { useAuth } from './hooks/useAuth';
import Layout from './components/layout/Layout';
import LoginPage from './pages/auth/LoginPage';
import AuthenticatedAppRoutes from './AuthenticatedAppRoutes';
import ReferencesAutoRefresh from './components/common/ReferencesAutoRefresh';
import 'antd/dist/reset.css';
import './styles/globals.css';

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const theme = useSelector((state: any) => state.theme?.theme || 'dark');

  React.useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  if (isLoading) {
    return <div className="flex items-center justify-center h-screen">Загрузка...</div>;
  }

  // Dev bypass is opt-in only; default behavior must respect auth state.
  const isDevAuthBypassEnabled = String(process.env.REACT_APP_DEV_AUTH_BYPASS || '').toLowerCase() === 'true';
  const canAccess = isAuthenticated || isDevAuthBypassEnabled;

  return (
    <ConfigProvider
      locale={ruRU}
      theme={{
        algorithm: theme === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#2563EB',
          borderRadius: 10,
          colorBgBase: '#F8FAFC',
          colorBgContainer: '#FFFFFF',
          colorText: '#111827',
          colorTextSecondary: '#4b5563',
          colorBorder: '#E5E7EB',
          fontSize: 15,
          fontSizeLG: 16,
          /* Базовый размер заголовков секций (в токенах Ant Design — уровень 3) */
          fontSizeHeading3: 20,
        },
      }}
    >
      <ReferencesAutoRefresh />
      <Router>
        {canAccess ? (
          <Layout>
            <AuthenticatedAppRoutes />
          </Layout>
        ) : (
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        )}
      </Router>
    </ConfigProvider>
  );
};

const App: React.FC = () => (
  <Provider store={store}>
    <AppContent />
  </Provider>
);

export default App;
